<template>
  <div class="area-card">
    <!-- Conteúdo do objetivo (variáveis manipuladas + restrições) — sem cabeçalho de área. -->
    <div class="area-body">

      <!-- Variables group header -->
      <div class="group-header" @click="isVariablesOpen = !isVariablesOpen">
        <b-icon icon="sliders" class="group-header-icon" />
        <span class="group-header-label">Manipuladas</span>
        <b-icon :icon="isVariablesOpen ? 'chevron-down' : 'chevron-right'" class="group-header-chevron" />
      </div>

      <!-- Variables group content -->
      <transition name="card-slide">
      <div v-show="isVariablesOpen" class="group-content">
        <template v-for="v in area.variables">
          <!-- Edit form -->
          <div v-if="editingVarId === v.id" :key="'edit-' + v.id" class="var-add-form">
            <div class="var-add-field">
              <label class="var-add-label">Origem</label>
              <div class="origin-toggle">
                <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': editVarOrigin === 'Corrente' }" @click="setEditVarOrigin('Corrente')">Corrente</button>
                <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': editVarOrigin === 'Operação' }" @click="setEditVarOrigin('Operação')">Operação</button>
              </div>
            </div>
            <template v-if="editVarOrigin === 'Corrente'">
              <div class="var-add-field">
                <label class="var-add-label">Corrente</label>
                <SearchableSelect
                  :modelRef="editVarCorrente"
                  :optList="correnteList"
                  optValue="name"
                  optDisplay="name"
                  :inputFunc="setEditVarCorrente"
                  placeholder="Selecione uma corrente..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editVarErrors && !editVarCorrente" class="var-field-error">Selecione uma corrente</span>
              </div>
              <div v-if="editVarCorrente" class="var-add-field">
                <label class="var-add-label">Propriedade</label>
                <SearchableSelect
                  :modelRef="editVarPropriedade ? editVarPropriedade.name : null"
                  :optList="editVarPropriedadeList"
                  optValue="name"
                  optDisplay="name"
                  :inputFunc="setEditVarPropriedade"
                  placeholder="Selecione uma propriedade..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editVarErrors && !editVarPropriedade" class="var-field-error">Selecione uma propriedade</span>
              </div>
              <div v-if="editVarPropriedade" class="var-add-field">
                <label class="var-add-label">Atributo</label>
                <SearchableSelect
                  :modelRef="editVarAtributo"
                  :optList="editVarAtributoList"
                  optValue="key"
                  optDisplay="key"
                  :inputFunc="setEditVarAtributo"
                  placeholder="Selecione um atributo..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editVarErrors && !editVarAtributo" class="var-field-error">Selecione um atributo</span>
              </div>
            </template>
            <template v-else>
              <div class="var-add-field">
                <label class="var-add-label">Operação</label>
                <SearchableSelect
                  :modelRef="editVarOperation"
                  :optList="operationList"
                  optValue="value"
                  optDisplay="display"
                  :inputFunc="setEditVarOperation"
                  placeholder="Selecione uma operação..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editVarErrors && !editVarOperation" class="var-field-error">Selecione uma operação</span>
              </div>
              <div v-if="editVarOperation" class="var-add-field">
                <label class="var-add-label">Atributo</label>
                <SearchableSelect
                  :modelRef="editVarOperationAtributo"
                  :optList="editVarOperationAtributoList"
                  optValue="key"
                  optDisplay="key"
                  :inputFunc="setEditVarOperationAtributo"
                  placeholder="Selecione um atributo..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editVarErrors && !editVarOperationAtributo" class="var-field-error">Selecione um atributo</span>
              </div>
            </template>
            <div v-if="(editVarOrigin === 'Corrente' && editVarAtributo) || (editVarOrigin === 'Operação' && editVarOperationAtributo)" class="var-add-field">
              <label class="var-add-label">Nome Personalizado <span class="var-add-optional">(opcional)</span></label>
              <input v-model="editVarNickname" class="var-add-input" placeholder="Nome personalizado..." />
            </div>
            <div class="var-add-field">
              <label class="var-add-label">Mínimo</label>
              <input type="number" v-model.number="editVarMin" class="var-add-input" placeholder="0" />
            </div>
            <div class="var-add-field">
              <label class="var-add-label">Máximo</label>
              <input type="number" v-model.number="editVarMax" class="var-add-input" placeholder="100" />
            </div>
            <div class="var-add-actions">
              <button class="var-add-btn var-add-btn--confirm" @click="confirmEditVariable(v.id)">
                <b-icon icon="check" class="mr-1" /> Confirmar
              </button>
              <button class="var-add-btn var-add-btn--cancel" @click="cancelEditVariable">
                Cancelar
              </button>
            </div>
          </div>
          <!-- Collapsed row -->
          <div v-else :key="v.id" class="var-item">
            <b-icon icon="pencil" class="var-edit-icon" @click="startEditVariable(v)" />
            <span class="var-label" :title="v.label" @click="startEditVariable(v)">{{ v.label }}</span>
            <b-icon v-if="!v.mapping" icon="exclamation-triangle" class="var-no-mapping-icon" title="Sem mapeamento RTO — edite e selecione corrente/propriedade/atributo" />
            <span class="var-range-text">{{ v.min }}</span>
            <span class="var-sep">–</span>
            <span class="var-range-text">{{ v.max }}</span>
            <button class="area-icon-btn area-icon-btn--danger" @click="requestRemoveVariable(v)">
              <b-icon icon="x" />
            </button>
          </div>
        </template>

        <!-- Add variable form -->
        <div v-if="isAddingVariable" class="var-add-form">
          <div v-if="!singletonLoaded" class="cascade-not-loaded">
            <b-icon icon="exclamation-triangle" class="cascade-not-loaded-icon" />
            <span>Execute uma simulação para habilitar os campos de corrente.</span>
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Origem</label>
            <div class="origin-toggle">
              <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': newVarOrigin === 'Corrente' }" @click="setNewVarOrigin('Corrente')">Corrente</button>
              <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': newVarOrigin === 'Operação' }" @click="setNewVarOrigin('Operação')">Operação</button>
            </div>
          </div>
          <template v-if="newVarOrigin === 'Corrente'">
            <div class="var-add-field">
              <label class="var-add-label">Corrente</label>
              <SearchableSelect
                :modelRef="newVarCorrente"
                :optList="correnteList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setNewVarCorrente"
                placeholder="Selecione uma corrente..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newVarErrors && !newVarCorrente" class="var-field-error">Selecione uma corrente</span>
            </div>
            <div v-if="newVarCorrente" class="var-add-field">
              <label class="var-add-label">Propriedade</label>
              <SearchableSelect
                :modelRef="newVarPropriedade ? newVarPropriedade.name : null"
                :optList="newVarPropriedadeList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setNewVarPropriedade"
                placeholder="Selecione uma propriedade..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newVarErrors && !newVarPropriedade" class="var-field-error">Selecione uma propriedade</span>
            </div>
            <div v-if="newVarPropriedade" class="var-add-field">
              <label class="var-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newVarAtributo"
                :optList="newVarAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setNewVarAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newVarErrors && !newVarAtributo" class="var-field-error">Selecione um atributo</span>
            </div>
          </template>
          <template v-else>
            <div class="var-add-field">
              <label class="var-add-label">Operação</label>
              <SearchableSelect
                :modelRef="newVarOperation"
                :optList="operationList"
                optValue="value"
                optDisplay="display"
                :inputFunc="setNewVarOperation"
                placeholder="Selecione uma operação..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newVarErrors && !newVarOperation" class="var-field-error">Selecione uma operação</span>
            </div>
            <div v-if="newVarOperation" class="var-add-field">
              <label class="var-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newVarOperationAtributo"
                :optList="newVarOperationAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setNewVarOperationAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newVarErrors && !newVarOperationAtributo" class="var-field-error">Selecione um atributo</span>
            </div>
          </template>
          <div v-if="(newVarOrigin === 'Corrente' && newVarAtributo) || (newVarOrigin === 'Operação' && newVarOperationAtributo)" class="var-add-field">
            <label class="var-add-label">Nome Personalizado <span class="var-add-optional">(opcional)</span></label>
            <input v-model="newVarNickname" class="var-add-input" placeholder="Nome personalizado..." />
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Mínimo</label>
            <input type="number" v-model.number="newVarMin" class="var-add-input" placeholder="0" />
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Máximo</label>
            <input type="number" v-model.number="newVarMax" class="var-add-input" placeholder="100" />
          </div>
          <div class="var-add-actions">
            <button class="var-add-btn var-add-btn--confirm" @click="confirmAddVariable">
              <b-icon icon="check" class="mr-1" /> Confirmar
            </button>
            <button class="var-add-btn var-add-btn--cancel" @click="cancelAddVariable">
              Cancelar
            </button>
          </div>
        </div>
        <button v-else class="area-add-btn" @click="isAddingVariable = true">
          <b-icon icon="plus" class="mr-1" /> Adicionar Variável
        </button>
      </div>

      </transition>

      <!-- Restrictions group header -->
      <div class="group-header" @click="isRestrictionsOpen = !isRestrictionsOpen">
        <b-icon icon="shield-check" class="group-header-icon" />
        <span class="group-header-label">Restrições</span>
        <b-icon :icon="isRestrictionsOpen ? 'chevron-down' : 'chevron-right'" class="group-header-chevron" />
      </div>

      <!-- Restrictions group content -->
      <transition name="card-slide">
      <div v-show="isRestrictionsOpen" class="group-content">
        <template v-for="r in (area.restrictions || [])">
          <!-- Edit form -->
          <div v-if="editingRestrId === r.id" :key="'restr-edit-' + r.id" class="var-add-form">
            <div class="var-add-field">
              <label class="var-add-label">Origem</label>
              <div class="restr-indicator-row">
                <input type="checkbox" v-model="editRestrEnabled" class="restr-checkbox" />
                <div class="origin-toggle">
                  <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': editRestrOrigin === 'Corrente' }" @click="setEditRestrOrigin('Corrente')">Corrente</button>
                  <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': editRestrOrigin === 'Operação' }" @click="setEditRestrOrigin('Operação')">Operação</button>
                </div>
              </div>
            </div>
            <template v-if="editRestrOrigin === 'Corrente'">
              <div class="var-add-field">
                <label class="var-add-label">Corrente</label>
                <SearchableSelect
                  :modelRef="editRestrCorrente"
                  :optList="correnteList"
                  optValue="name"
                  optDisplay="name"
                  :inputFunc="setEditRestrCorrente"
                  placeholder="Selecione uma corrente..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editRestrErrors && !editRestrCorrente" class="var-field-error">Selecione uma corrente</span>
              </div>
              <div v-if="editRestrCorrente" class="var-add-field">
                <label class="var-add-label">Propriedade</label>
                <SearchableSelect
                  :modelRef="editRestrPropriedade ? editRestrPropriedade.name : null"
                  :optList="editRestrPropriedadeList"
                  optValue="name"
                  optDisplay="name"
                  :inputFunc="setEditRestrPropriedade"
                  placeholder="Selecione uma propriedade..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editRestrErrors && !editRestrPropriedade" class="var-field-error">Selecione uma propriedade</span>
              </div>
              <div v-if="editRestrPropriedade" class="var-add-field">
                <label class="var-add-label">Atributo</label>
                <SearchableSelect
                  :modelRef="editRestrAtributo"
                  :optList="editRestrAtributoList"
                  optValue="key"
                  optDisplay="key"
                  :inputFunc="setEditRestrAtributo"
                  placeholder="Selecione um atributo..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editRestrErrors && !editRestrAtributo" class="var-field-error">Selecione um atributo</span>
              </div>
            </template>
            <template v-else>
              <div class="var-add-field">
                <label class="var-add-label">Operação</label>
                <SearchableSelect
                  :modelRef="editRestrOperation"
                  :optList="operationList"
                  optValue="value"
                  optDisplay="display"
                  :inputFunc="setEditRestrOperation"
                  placeholder="Selecione uma operação..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editRestrErrors && !editRestrOperation" class="var-field-error">Selecione uma operação</span>
              </div>
              <div v-if="editRestrOperation" class="var-add-field">
                <label class="var-add-label">Atributo</label>
                <SearchableSelect
                  :modelRef="editRestrOperationAtributo"
                  :optList="editRestrOperationAtributoList"
                  optValue="key"
                  optDisplay="key"
                  :inputFunc="setEditRestrOperationAtributo"
                  placeholder="Selecione um atributo..."
                  :clearable="true"
                  extraClass="var-select"
                />
                <span v-if="editRestrErrors && !editRestrOperationAtributo" class="var-field-error">Selecione um atributo</span>
              </div>
            </template>
            <div v-if="(editRestrOrigin === 'Corrente' && editRestrAtributo) || (editRestrOrigin === 'Operação' && editRestrOperationAtributo)" class="var-add-field">
              <label class="var-add-label">Nome Personalizado <span class="var-add-optional">(opcional)</span></label>
              <input v-model="editRestrNickname" class="var-add-input" placeholder="Nome personalizado..." />
            </div>
            <div class="var-add-field">
              <label class="var-add-label">Mínimo</label>
              <input type="number" v-model.number="editRestrMin" class="var-add-input" placeholder="0" />
            </div>
            <div class="var-add-field">
              <label class="var-add-label">Máximo</label>
              <input type="number" v-model.number="editRestrMax" class="var-add-input" placeholder="100" />
            </div>
            <div class="var-add-field">
              <label class="var-add-label">Penalty Factor</label>
              <input type="number" step="any" v-model.number="editRestrPenalty" class="var-add-input" placeholder="7000" />
            </div>
            <div class="var-add-actions">
              <button class="var-add-btn var-add-btn--confirm" @click="confirmEditRestriction(r.id)">
                <b-icon icon="check" class="mr-1" /> Confirmar
              </button>
              <button class="var-add-btn var-add-btn--cancel" @click="cancelEditRestriction">
                Cancelar
              </button>
            </div>
          </div>
          <!-- Collapsed row -->
          <div v-else :key="r.id" class="var-item">
            <input type="checkbox" :checked="r.enabled" @change="updateRestriction(r.id, { enabled: $event.target.checked })" class="restr-checkbox" />
            <b-icon icon="pencil" class="var-edit-icon" @click="startEditRestriction(r)" />
            <span class="var-label" :title="r.label" @click="startEditRestriction(r)">{{ r.label }}</span>
            <b-icon v-if="!r.mapping" icon="exclamation-triangle" class="var-no-mapping-icon" title="Sem mapeamento RTO — edite e selecione corrente/propriedade/atributo" />
            <span class="var-range-text">{{ r.min }}</span>
            <span class="var-sep">–</span>
            <span class="var-range-text">{{ r.max }}</span>
            <button class="area-icon-btn area-icon-btn--danger" @click="requestRemoveRestriction(r)">
              <b-icon icon="x" />
            </button>
          </div>
        </template>

        <!-- Add restriction form -->
        <div v-if="isAddingRestriction" class="var-add-form">
          <div v-if="!singletonLoaded" class="cascade-not-loaded">
            <b-icon icon="exclamation-triangle" class="cascade-not-loaded-icon" />
            <span>Execute uma simulação para habilitar os campos de corrente.</span>
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Origem</label>
            <div class="restr-indicator-row">
              <input type="checkbox" v-model="newRestrEnabled" class="restr-checkbox" />
              <div class="origin-toggle">
                <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': newRestrOrigin === 'Corrente' }" @click="setNewRestrOrigin('Corrente')">Corrente</button>
                <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': newRestrOrigin === 'Operação' }" @click="setNewRestrOrigin('Operação')">Operação</button>
              </div>
            </div>
          </div>
          <template v-if="newRestrOrigin === 'Corrente'">
            <div class="var-add-field">
              <label class="var-add-label">Corrente</label>
              <SearchableSelect
                :modelRef="newRestrCorrente"
                :optList="correnteList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setNewRestrCorrente"
                placeholder="Selecione uma corrente..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newRestrErrors && !newRestrCorrente" class="var-field-error">Selecione uma corrente</span>
            </div>
            <div v-if="newRestrCorrente" class="var-add-field">
              <label class="var-add-label">Propriedade</label>
              <SearchableSelect
                :modelRef="newRestrPropriedade ? newRestrPropriedade.name : null"
                :optList="newRestrPropriedadeList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setNewRestrPropriedade"
                placeholder="Selecione uma propriedade..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newRestrErrors && !newRestrPropriedade" class="var-field-error">Selecione uma propriedade</span>
            </div>
            <div v-if="newRestrPropriedade" class="var-add-field">
              <label class="var-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newRestrAtributo"
                :optList="newRestrAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setNewRestrAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newRestrErrors && !newRestrAtributo" class="var-field-error">Selecione um atributo</span>
            </div>
          </template>
          <template v-else>
            <div class="var-add-field">
              <label class="var-add-label">Operação</label>
              <SearchableSelect
                :modelRef="newRestrOperation"
                :optList="operationList"
                optValue="value"
                optDisplay="display"
                :inputFunc="setNewRestrOperation"
                placeholder="Selecione uma operação..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newRestrErrors && !newRestrOperation" class="var-field-error">Selecione uma operação</span>
            </div>
            <div v-if="newRestrOperation" class="var-add-field">
              <label class="var-add-label">Atributo</label>
              <SearchableSelect
                :modelRef="newRestrOperationAtributo"
                :optList="newRestrOperationAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setNewRestrOperationAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="var-select"
              />
              <span v-if="newRestrErrors && !newRestrOperationAtributo" class="var-field-error">Selecione um atributo</span>
            </div>
          </template>
          <div v-if="(newRestrOrigin === 'Corrente' && newRestrAtributo) || (newRestrOrigin === 'Operação' && newRestrOperationAtributo)" class="var-add-field">
            <label class="var-add-label">Nome Personalizado <span class="var-add-optional">(opcional)</span></label>
            <input v-model="newRestrNickname" class="var-add-input" placeholder="Nome personalizado..." />
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Mínimo</label>
            <input type="number" v-model.number="newRestrMin" class="var-add-input" placeholder="0" />
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Máximo</label>
            <input type="number" v-model.number="newRestrMax" class="var-add-input" placeholder="100" />
          </div>
          <div class="var-add-field">
            <label class="var-add-label">Penalty Factor</label>
            <input type="number" step="any" v-model.number="newRestrPenalty" class="var-add-input" placeholder="7000" />
          </div>
          <div class="var-add-actions">
            <button class="var-add-btn var-add-btn--confirm" @click="confirmAddRestriction">
              <b-icon icon="check" class="mr-1" /> Confirmar
            </button>
            <button class="var-add-btn var-add-btn--cancel" @click="cancelAddRestriction">
              Cancelar
            </button>
          </div>
        </div>
        <button v-else class="area-add-btn" @click="isAddingRestriction = true">
          <b-icon icon="plus" class="mr-1" /> Adicionar Restrição
        </button>
      </div>
      </transition>

    </div>

    <!-- Delete confirmation modal -->
    <ConfirmDeleteModal
      :visible="deleteModal.visible"
      :message="deleteModal.message"
      :variables="deleteModal.variables"
      :restrictions="deleteModal.restrictions"
      @confirm="confirmDelete"
      @cancel="deleteModal.visible = false"
    />
  </div>
</template>

<script>
import SearchableSelect from '../rta/commons/SearchableSelect.vue';
import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
import { SingletonEnginnering } from '../flowchart/nodes/_service/singletonEngineering';
import EngineeringMapping from '../flowchart/nodes/_service/singletonTools/engineeringMapping';
import { safeRandomUUID } from '@/services/uuid';

export default {
  name: 'RtoObjectiveArea',

  components: { SearchableSelect, ConfirmDeleteModal },

  props: {
    area: { type: Object, required: true },
  },

  emits: ['update'],

  data() {
    return {
      isVariablesOpen: false,
      isRestrictionsOpen: false,
      // Add variable form
      isAddingVariable: false,
      newVarOrigin: 'Corrente',
      newVarCorrente: null,
      newVarPropriedade: null,
      newVarAtributo: null,
      newVarOperation: null,
      newVarOperationAtributo: null,
      newVarNickname: '',
      newVarMin: 0,
      newVarMax: 100,
      newVarErrors: false,
      // Edit variable form
      editingVarId: null,
      editVarOrigin: 'Corrente',
      editVarCorrente: null,
      editVarPropriedade: null,
      editVarAtributo: null,
      editVarOperation: null,
      editVarOperationAtributo: null,
      editVarNickname: '',
      editVarMin: 0,
      editVarMax: 100,
      editVarErrors: false,
      // Add restriction form
      isAddingRestriction: false,
      newRestrOrigin: 'Corrente',
      newRestrCorrente: null,
      newRestrPropriedade: null,
      newRestrAtributo: null,
      newRestrOperation: null,
      newRestrOperationAtributo: null,
      newRestrNickname: '',
      newRestrEnabled: true,
      newRestrMin: 0,
      newRestrMax: 100,
      newRestrPenalty: 7000,
      newRestrErrors: false,
      // Edit restriction form
      editingRestrId: null,
      editRestrOrigin: 'Corrente',
      editRestrCorrente: null,
      editRestrPropriedade: null,
      editRestrAtributo: null,
      editRestrOperation: null,
      editRestrOperationAtributo: null,
      editRestrNickname: '',
      editRestrEnabled: true,
      editRestrMin: 0,
      editRestrMax: 100,
      editRestrPenalty: 7000,
      editRestrErrors: false,
      deleteModal: {
        visible: false,
        message: '',
        variables: [],
        restrictions: [],
        onConfirm: null,
      },
    };
  },

  computed: {
    singletonLoaded() {
      return SingletonEnginnering.isLoaded && !!SingletonEnginnering.currents;
    },

    correnteList() {
      if (!this.singletonLoaded) return [];
      return SingletonEnginnering.currents.map(c => ({ name: c }));
    },

    operationList() {
      if (!this.singletonLoaded || !SingletonEnginnering.operations) return [];
      return SingletonEnginnering.operations.map(o => ({
        value: o,
        display: o.replace('|', ' / '),
      }));
    },

    newVarOperationAtributoList() {
      if (!this.newVarOperation) return [];
      try { return SingletonEnginnering.getOperationsProperties(this.newVarOperation); }
      catch { return []; }
    },

    editVarOperationAtributoList() {
      if (!this.editVarOperation) return [];
      try { return SingletonEnginnering.getOperationsProperties(this.editVarOperation); }
      catch { return []; }
    },

    newRestrOperationAtributoList() {
      if (!this.newRestrOperation) return [];
      try { return SingletonEnginnering.getOperationsProperties(this.newRestrOperation); }
      catch { return []; }
    },

    editRestrOperationAtributoList() {
      if (!this.editRestrOperation) return [];
      try { return SingletonEnginnering.getOperationsProperties(this.editRestrOperation); }
      catch { return []; }
    },

    newVarPropriedadeList() {
      if (!this.newVarCorrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.newVarCorrente); }
      catch { return []; }
    },

    newVarAtributoList() {
      if (!this.newVarCorrente || !this.newVarPropriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.newVarCorrente, this.newVarPropriedade); }
      catch { return []; }
    },

    editVarPropriedadeList() {
      if (!this.editVarCorrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.editVarCorrente); }
      catch { return []; }
    },

    editVarAtributoList() {
      if (!this.editVarCorrente || !this.editVarPropriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.editVarCorrente, this.editVarPropriedade); }
      catch { return []; }
    },

    newRestrPropriedadeList() {
      if (!this.newRestrCorrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.newRestrCorrente); }
      catch { return []; }
    },

    newRestrAtributoList() {
      if (!this.newRestrCorrente || !this.newRestrPropriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.newRestrCorrente, this.newRestrPropriedade); }
      catch { return []; }
    },

    editRestrPropriedadeList() {
      if (!this.editRestrCorrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.editRestrCorrente); }
      catch { return []; }
    },

    editRestrAtributoList() {
      if (!this.editRestrCorrente || !this.editRestrPropriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.editRestrCorrente, this.editRestrPropriedade); }
      catch { return []; }
    },
  },

  methods: {
    computeMapping(origin, corrente, propriedade, atributo, operation, operationAtributo) {
      if (origin === 'Operação') {
        if (!operation || !operationAtributo) return '';
        return `op|${operation}|Atributos|${operationAtributo}`;
      }
      if (!corrente || !propriedade || !atributo) return '';
      return EngineeringMapping.setMapping({
        type: 'Correntes',
        index: corrente,
        propName: propriedade.name,
        key: atributo,
      });
    },

    setNewVarOrigin(origin) {
      this.newVarOrigin = origin;
      this.newVarCorrente = null;
      this.newVarPropriedade = null;
      this.newVarAtributo = null;
      this.newVarOperation = null;
      this.newVarOperationAtributo = null;
    },

    setEditVarOrigin(origin) {
      this.editVarOrigin = origin;
      this.editVarCorrente = null;
      this.editVarPropriedade = null;
      this.editVarAtributo = null;
      this.editVarOperation = null;
      this.editVarOperationAtributo = null;
    },

    setNewRestrOrigin(origin) {
      this.newRestrOrigin = origin;
      this.newRestrCorrente = null;
      this.newRestrPropriedade = null;
      this.newRestrAtributo = null;
      this.newRestrOperation = null;
      this.newRestrOperationAtributo = null;
    },

    setEditRestrOrigin(origin) {
      this.editRestrOrigin = origin;
      this.editRestrCorrente = null;
      this.editRestrPropriedade = null;
      this.editRestrAtributo = null;
      this.editRestrOperation = null;
      this.editRestrOperationAtributo = null;
    },

    setNewVarOperation(e) {
      this.newVarOperation = e.target.value || null;
      this.newVarOperationAtributo = null;
    },

    setNewVarOperationAtributo(e) {
      this.newVarOperationAtributo = e.target.value || null;
    },

    setEditVarOperation(e) {
      this.editVarOperation = e.target.value || null;
      this.editVarOperationAtributo = null;
    },

    setEditVarOperationAtributo(e) {
      this.editVarOperationAtributo = e.target.value || null;
    },

    setNewRestrOperation(e) {
      this.newRestrOperation = e.target.value || null;
      this.newRestrOperationAtributo = null;
    },

    setNewRestrOperationAtributo(e) {
      this.newRestrOperationAtributo = e.target.value || null;
    },

    setEditRestrOperation(e) {
      this.editRestrOperation = e.target.value || null;
      this.editRestrOperationAtributo = null;
    },

    setEditRestrOperationAtributo(e) {
      this.editRestrOperationAtributo = e.target.value || null;
    },

    requestRemoveVariable(v) {
      this.deleteModal = {
        visible: true,
        message: `Tem certeza que deseja excluir a variável "${v.label}"?`,
        variables: [],
        restrictions: [],
        onConfirm: () => this.removeVariable(v.id),
      };
    },

    confirmDelete() {
      this.deleteModal.onConfirm?.();
      this.deleteModal.visible = false;
    },

    // --- Variable cascade setters ---

    setNewVarCorrente(e) {
      this.newVarCorrente = e.target.value || null;
      this.newVarPropriedade = null;
      this.newVarAtributo = null;
    },

    setNewVarPropriedade(e) {
      const name = e.target.value;
      this.newVarPropriedade = name ? (this.newVarPropriedadeList.find(p => p.name === name) || null) : null;
      this.newVarAtributo = null;
    },

    setNewVarAtributo(e) {
      this.newVarAtributo = e.target.value || null;
    },

    setEditVarCorrente(e) {
      this.editVarCorrente = e.target.value || null;
      this.editVarPropriedade = null;
      this.editVarAtributo = null;
    },

    setEditVarPropriedade(e) {
      const name = e.target.value;
      this.editVarPropriedade = name ? (this.editVarPropriedadeList.find(p => p.name === name) || null) : null;
      this.editVarAtributo = null;
    },

    setEditVarAtributo(e) {
      this.editVarAtributo = e.target.value || null;
    },

    // --- Variable actions ---

    startEditVariable(v) {
      this.editingVarId = v.id;
      const isOp = v.origin === 'Operação' || (v.mapping && v.mapping.startsWith('op|'));
      this.editVarOrigin = isOp ? 'Operação' : 'Corrente';
      if (isOp) {
        this.editVarCorrente = null;
        this.editVarPropriedade = null;
        this.editVarAtributo = null;
        this.editVarOperation = v.operation || null;
        this.editVarOperationAtributo = v.operationAtributo || v.atributo || null;
      } else {
        this.editVarCorrente = v.corrente || null;
        this.editVarPropriedade = v.propriedade || null;
        this.editVarAtributo = v.atributo || null;
        this.editVarOperation = null;
        this.editVarOperationAtributo = null;
      }
      const sourceLabel = isOp ? (v.operation || '').replace('|', ' / ') : v.corrente;
      this.editVarNickname = (v.label && sourceLabel && v.label !== sourceLabel) ? v.label : '';
      this.editVarMin = v.min;
      this.editVarMax = v.max;
    },

    confirmEditVariable(id) {
      const isOp = this.editVarOrigin === 'Operação';
      if (isOp ? !this.editVarOperationAtributo : !this.editVarAtributo) {
        this.editVarErrors = true; return;
      }
      const sourceLabel = isOp ? (this.editVarOperation || '').replace('|', ' / ') : this.editVarCorrente;
      this.updateVariable(id, {
        origin: this.editVarOrigin,
        corrente: isOp ? null : this.editVarCorrente,
        propriedade: isOp ? null : this.editVarPropriedade,
        atributo: isOp ? this.editVarOperationAtributo : this.editVarAtributo,
        operation: isOp ? this.editVarOperation : null,
        operationAtributo: isOp ? this.editVarOperationAtributo : null,
        label: this.editVarNickname.trim() || sourceLabel,
        min: this.editVarMin,
        max: this.editVarMax,
        mapping: this.computeMapping(this.editVarOrigin, this.editVarCorrente, this.editVarPropriedade, this.editVarAtributo, this.editVarOperation, this.editVarOperationAtributo),
      });
      this.cancelEditVariable();
    },

    cancelEditVariable() {
      this.editingVarId = null;
      this.editVarOrigin = 'Corrente';
      this.editVarCorrente = null;
      this.editVarPropriedade = null;
      this.editVarAtributo = null;
      this.editVarOperation = null;
      this.editVarOperationAtributo = null;
      this.editVarNickname = '';
      this.editVarMin = 0;
      this.editVarMax = 100;
      this.editVarErrors = false;
    },

    confirmAddVariable() {
      const isOp = this.newVarOrigin === 'Operação';
      if (isOp ? !this.newVarOperationAtributo : !this.newVarAtributo) {
        this.newVarErrors = true; return;
      }
      const sourceLabel = isOp ? (this.newVarOperation || '').replace('|', ' / ') : this.newVarCorrente;
      this.$emit('update', {
        ...this.area,
        variables: [
          ...this.area.variables,
          {
            id: safeRandomUUID(),
            origin: this.newVarOrigin,
            corrente: isOp ? null : this.newVarCorrente,
            propriedade: isOp ? null : this.newVarPropriedade,
            atributo: isOp ? this.newVarOperationAtributo : this.newVarAtributo,
            operation: isOp ? this.newVarOperation : null,
            operationAtributo: isOp ? this.newVarOperationAtributo : null,
            label: this.newVarNickname.trim() || sourceLabel,
            min: this.newVarMin,
            max: this.newVarMax,
            mapping: this.computeMapping(this.newVarOrigin, this.newVarCorrente, this.newVarPropriedade, this.newVarAtributo, this.newVarOperation, this.newVarOperationAtributo),
          },
        ],
      });
      this.cancelAddVariable();
    },

    cancelAddVariable() {
      this.newVarOrigin = 'Corrente';
      this.newVarCorrente = null;
      this.newVarPropriedade = null;
      this.newVarAtributo = null;
      this.newVarOperation = null;
      this.newVarOperationAtributo = null;
      this.newVarNickname = '';
      this.newVarMin = 0;
      this.newVarMax = 100;
      this.newVarErrors = false;
      this.isAddingVariable = false;
    },

    updateVariable(id, changes) {
      const variables = this.area.variables.map(v =>
        v.id === id ? { ...v, ...changes } : v
      );
      this.$emit('update', { ...this.area, variables });
    },

    removeVariable(id) {
      this.$emit('update', {
        ...this.area,
        variables: this.area.variables.filter(v => v.id !== id),
      });
    },

    // --- Restriction cascade setters ---

    setNewRestrCorrente(e) {
      this.newRestrCorrente = e.target.value || null;
      this.newRestrPropriedade = null;
      this.newRestrAtributo = null;
    },

    setNewRestrPropriedade(e) {
      const name = e.target.value;
      this.newRestrPropriedade = name ? (this.newRestrPropriedadeList.find(p => p.name === name) || null) : null;
      this.newRestrAtributo = null;
    },

    setNewRestrAtributo(e) {
      this.newRestrAtributo = e.target.value || null;
    },

    setEditRestrCorrente(e) {
      this.editRestrCorrente = e.target.value || null;
      this.editRestrPropriedade = null;
      this.editRestrAtributo = null;
    },

    setEditRestrPropriedade(e) {
      const name = e.target.value;
      this.editRestrPropriedade = name ? (this.editRestrPropriedadeList.find(p => p.name === name) || null) : null;
      this.editRestrAtributo = null;
    },

    setEditRestrAtributo(e) {
      this.editRestrAtributo = e.target.value || null;
    },

    // --- Restriction actions ---

    startEditRestriction(r) {
      this.editingRestrId = r.id;
      const isOp = r.origin === 'Operação' || (r.mapping && r.mapping.startsWith('op|'));
      this.editRestrOrigin = isOp ? 'Operação' : 'Corrente';
      if (isOp) {
        this.editRestrCorrente = null;
        this.editRestrPropriedade = null;
        this.editRestrAtributo = null;
        this.editRestrOperation = r.operation || null;
        this.editRestrOperationAtributo = r.operationAtributo || r.atributo || null;
      } else {
        this.editRestrCorrente = r.corrente || null;
        this.editRestrPropriedade = r.propriedade || null;
        this.editRestrAtributo = r.atributo || null;
        this.editRestrOperation = null;
        this.editRestrOperationAtributo = null;
      }
      const sourceLabel = isOp ? (r.operation || '').replace('|', ' / ') : r.corrente;
      this.editRestrNickname = (r.label && sourceLabel && r.label !== sourceLabel) ? r.label : '';
      this.editRestrEnabled = r.enabled;
      this.editRestrMin = r.min;
      this.editRestrMax = r.max;
      this.editRestrPenalty = r.penaltyFactor ?? 7000;
    },

    confirmEditRestriction(id) {
      const isOp = this.editRestrOrigin === 'Operação';
      if (isOp ? !this.editRestrOperationAtributo : !this.editRestrAtributo) {
        this.editRestrErrors = true; return;
      }
      const sourceLabel = isOp ? (this.editRestrOperation || '').replace('|', ' / ') : this.editRestrCorrente;
      this.updateRestriction(id, {
        origin: this.editRestrOrigin,
        corrente: isOp ? null : this.editRestrCorrente,
        propriedade: isOp ? null : this.editRestrPropriedade,
        atributo: isOp ? this.editRestrOperationAtributo : this.editRestrAtributo,
        operation: isOp ? this.editRestrOperation : null,
        operationAtributo: isOp ? this.editRestrOperationAtributo : null,
        label: this.editRestrNickname.trim() || sourceLabel,
        enabled: this.editRestrEnabled,
        min: this.editRestrMin,
        max: this.editRestrMax,
        penaltyFactor: this.editRestrPenalty,
        mapping: this.computeMapping(this.editRestrOrigin, this.editRestrCorrente, this.editRestrPropriedade, this.editRestrAtributo, this.editRestrOperation, this.editRestrOperationAtributo),
      });
      this.cancelEditRestriction();
    },

    cancelEditRestriction() {
      this.editingRestrId = null;
      this.editRestrOrigin = 'Corrente';
      this.editRestrCorrente = null;
      this.editRestrPropriedade = null;
      this.editRestrAtributo = null;
      this.editRestrOperation = null;
      this.editRestrOperationAtributo = null;
      this.editRestrNickname = '';
      this.editRestrEnabled = true;
      this.editRestrMin = 0;
      this.editRestrMax = 100;
      this.editRestrPenalty = 7000;
      this.editRestrErrors = false;
    },

    confirmAddRestriction() {
      const isOp = this.newRestrOrigin === 'Operação';
      if (isOp ? !this.newRestrOperationAtributo : !this.newRestrAtributo) {
        this.newRestrErrors = true; return;
      }
      const sourceLabel = isOp ? (this.newRestrOperation || '').replace('|', ' / ') : this.newRestrCorrente;
      this.$emit('update', {
        ...this.area,
        restrictions: [
          ...(this.area.restrictions || []),
          {
            id: safeRandomUUID(),
            origin: this.newRestrOrigin,
            corrente: isOp ? null : this.newRestrCorrente,
            propriedade: isOp ? null : this.newRestrPropriedade,
            atributo: isOp ? this.newRestrOperationAtributo : this.newRestrAtributo,
            operation: isOp ? this.newRestrOperation : null,
            operationAtributo: isOp ? this.newRestrOperationAtributo : null,
            label: this.newRestrNickname.trim() || sourceLabel,
            enabled: this.newRestrEnabled,
            min: this.newRestrMin,
            max: this.newRestrMax,
            penaltyFactor: this.newRestrPenalty,
            mapping: this.computeMapping(this.newRestrOrigin, this.newRestrCorrente, this.newRestrPropriedade, this.newRestrAtributo, this.newRestrOperation, this.newRestrOperationAtributo),
          },
        ],
      });
      this.cancelAddRestriction();
    },

    cancelAddRestriction() {
      this.newRestrOrigin = 'Corrente';
      this.newRestrCorrente = null;
      this.newRestrPropriedade = null;
      this.newRestrAtributo = null;
      this.newRestrOperation = null;
      this.newRestrOperationAtributo = null;
      this.newRestrNickname = '';
      this.newRestrEnabled = true;
      this.newRestrMin = 0;
      this.newRestrMax = 100;
      this.newRestrPenalty = 7000;
      this.newRestrErrors = false;
      this.isAddingRestriction = false;
    },

    updateRestriction(id, changes) {
      const restrictions = (this.area.restrictions || []).map(r =>
        r.id === id ? { ...r, ...changes } : r
      );
      this.$emit('update', { ...this.area, restrictions });
    },

    removeRestriction(id) {
      this.$emit('update', {
        ...this.area,
        restrictions: (this.area.restrictions || []).filter(r => r.id !== id),
      });
    },

    requestRemoveRestriction(r) {
      this.deleteModal = {
        visible: true,
        message: `Tem certeza que deseja excluir a restrição "${r.label}"?`,
        variables: [],
        restrictions: [],
        onConfirm: () => this.removeRestriction(r.id),
      };
    },
  },
};
</script>

<style scoped>
.area-card {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background-color: #fafafa;
  overflow: hidden;
}

/* --- Header --- */
.area-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 8px;
  background-color: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.area-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.area-folder-icon {
  color: #ff5f00;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.area-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.area-label:hover {
  text-decoration: underline dotted #9ca3af;
  cursor: text;
}

.area-rename-input {
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 2px 6px;
  width: 120px;
  color: #374151;
}

.area-rename-input:focus {
  outline: none;
  border-color: #ff5f00;
}

.area-header-right {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

/* --- Icon buttons --- */
.area-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 3px;
  padding: 3px;
  font-size: 0.78rem;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 100ms;
}

.area-icon-btn:hover {
  background-color: #e5e7eb;
}

.area-icon-btn--confirm { color: #16a34a; }
.area-icon-btn--confirm:hover { background-color: #dcfce7; }
.area-icon-btn--cancel { color: #6b7280; }
.area-icon-btn--danger { color: #ef4444; }
.area-icon-btn--danger:hover { background-color: #fee2e2; }

/* --- Body --- */
.area-body {
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* --- Variable item --- */
.var-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.var-edit-icon {
  font-size: 0.65rem;
  color: #ff5f00;
  flex-shrink: 0;
  cursor: pointer;
}

.var-label {
  flex: 1;
  min-width: 0;
  font-size: 0.72rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.var-label:hover {
  text-decoration: underline dotted #ff5f00;
}

.var-range-text {
  font-size: 0.72rem;
  color: #6b7280;
  flex-shrink: 0;
  white-space: nowrap;
}

.var-sep {
  font-size: 0.7rem;
  color: #9ca3af;
  flex-shrink: 0;
}

.var-no-mapping-icon {
  font-size: 0.65rem;
  color: #f59e0b;
  flex-shrink: 0;
  cursor: help;
}

/* --- Add variable form --- */
.var-add-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
}

.var-add-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.var-add-label {
  font-size: 0.68rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
}

.var-add-optional {
  font-weight: 400;
  color: #9ca3af;
}

.var-field-error {
  font-size: 0.65rem;
  color: #ef4444;
  margin-top: 2px;
}

.var-add-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 0.78rem;
  color: #374151;
}

.var-add-input:focus {
  outline: none;
  border-color: #ff5f00;
}

.var-add-form ::v-deep .select-shell {
  font-size: 0.78rem !important;
  border-color: #ff5f00 !important;
}

.var-add-form ::v-deep .select-shell:focus-within {
  box-shadow: 0 0 0 3px rgba(255, 95, 0, 0.2) !important;
}

.var-add-actions {
  display: flex;
  gap: 6px;
  padding-top: 2px;
}

.var-add-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border: none;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms;
}

.var-add-btn--confirm {
  background-color: #ff5f00;
  color: #ffffff;
}

.var-add-btn--confirm:hover {
  background-color: #e04e00;
}

.var-add-btn--cancel {
  background-color: #e5e7eb;
  color: #374151;
}

.var-add-btn--cancel:hover {
  background-color: #d1d5db;
}

/* --- Group headers --- */
.group-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 4px;
  border-radius: 3px;
  cursor: pointer;
  user-select: none;
  transition: background-color 100ms;
}

.group-header:hover {
  background-color: #f3f4f6;
}

.group-header-icon {
  font-size: 0.65rem;
  color: #ff5f00;
  flex-shrink: 0;
}

.group-header-label {
  flex: 1;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.group-header-chevron {
  font-size: 0.65rem;
  color: #9ca3af;
  flex-shrink: 0;
}

/* --- Group content --- */
.group-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 4px;
}

/* --- Restriction checkbox --- */
.restr-checkbox {
  flex-shrink: 0;
  accent-color: #ff5f00;
  width: 13px;
  height: 13px;
  cursor: pointer;
}

.restr-indicator-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.restr-indicator-row ::v-deep .select-shell {
  flex: 1;
}

/* --- Singleton not loaded warning --- */
.cascade-not-loaded {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background-color: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 3px;
  font-size: 0.68rem;
  color: #92400e;
}

.cascade-not-loaded-icon {
  color: #b45309;
  flex-shrink: 0;
  font-size: 0.75rem;
}

/* --- Add area button --- */
.area-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 5px;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #9ca3af;
  cursor: pointer;
  transition: color 150ms, border-color 150ms;
}

.area-add-btn:hover {
  color: #ff5f00;
  border-color: #ff5f00;
}

/* --- Card slide transition --- */
.card-slide-enter-active,
.card-slide-leave-active {
  transition: max-height 280ms ease, opacity 200ms ease;
  overflow: hidden;
}

.card-slide-enter,
.card-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.card-slide-enter-to,
.card-slide-leave {
  max-height: 1200px;
  opacity: 1;
}

.origin-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
}
.origin-toggle-btn {
  border: 0;
  background: #fff;
  color: #374151;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.origin-toggle-btn + .origin-toggle-btn {
  border-left: 1px solid #d1d5db;
}
.origin-toggle-btn:hover {
  background: #f3f4f6;
}
.origin-toggle-btn--active {
  background: #f97316;
  color: #fff;
  font-weight: 600;
}
.origin-toggle-btn--active:hover {
  background: #f97316;
}
</style>
